/**
 * Copyright 2017 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import React, { memo } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { getBackURL } from "openstack-uicore-foundation/lib/utils/methods";
import { doLogout } from "openstack-uicore-foundation/lib/security/actions";
import NavBar from "openstack-uicore-foundation/lib/components/mui/nav-bar";
import {
  doLoginBasicLogin,
  getIdToken,
  initLogOut
} from "openstack-uicore-foundation/lib/security/methods";
import { GlobalConfirmDialog } from "openstack-uicore-foundation/lib/components/mui/show-confirm-dialog";
import {
  lazyWithReload,
  initChunkErrorRecovery,
  chunkErrorSentryBeforeSend
} from "openstack-uicore-foundation/lib/utils/lazy-with-reload";
import IdTokenVerifier from "idtoken-verifier";
// eslint-disable-next-line
import * as Sentry from "@sentry/react";
import AuthorizedRoute from "./routes/authorized-route";
import AuthorizationCallbackRoute from "./routes/authorization-callback-route";
import LogOutCallback from "./routes/logout-callback-route";
import DefaultRoute from "./routes/default-route";
import { SentryFallbackFunction } from "./components/SentryErrorComponent";
import CustomTheme from "./styles/CustomTheme";
import "./styles/global-styles.scss";
import "./i18n";
import SnackbarNotification from "./components/SnackbarNotification";
import LoadingOverlay from "./components/LoadingOverlay";
import { BreadcrumbsProvider } from "./components/CustomBreadcrumbs/BreadcrumbsContext";

const PrimaryLayout = lazyWithReload(() => import("./layouts/primary-layout"));
const LoginPage = lazyWithReload(() => import("./pages/login-page"));
const CustomErrorPage = lazyWithReload(() => import("./pages/error-page"));

initChunkErrorRecovery();

// move all env var to global scope so ui core has access to this

window.IDP_BASE_URL = process.env.IDP_BASE_URL;
window.API_BASE_URL = process.env.API_BASE_URL;
window.FILE_UPLOAD_API_BASE_URL = process.env.FILE_UPLOAD_API_BASE_URL;
window.MARKETING_API_BASE_URL = process.env.MARKETING_API_BASE_URL;
window.EMAIL_API_BASE_URL = process.env.EMAIL_API_BASE_URL;
window.SPONSOR_USERS_API_BASE_URL = process.env.SPONSOR_USERS_API_BASE_URL;
window.PURCHASES_API_URL = process.env.PURCHASES_API_URL;
window.SPONSOR_PAGES_API_URL = process.env.SPONSOR_PAGES_API_URL;
window.OAUTH2_CLIENT_ID = process.env.OAUTH2_CLIENT_ID;
window.SCOPES = process.env.SCOPES;
window.ALLOWED_USER_GROUPS = process.env.ALLOWED_USER_GROUPS;
window.APP_CLIENT_NAME = process.env.APP_CLIENT_NAME;
window.OAUTH2_FLOW = process.env.OAUTH2_FLOW || "token id_token";
window.SENTRY_DSN = process.env.SENTRY_DSN;
window.SENTRY_TRACE_SAMPLE_RATE = process.env.SENTRY_TRACE_SAMPLE_RATE;
window.SENTRY_TRACE_PROPAGATION_TARGETS =
  process.env.SENTRY_TRACE_PROPAGATION_TARGETS;
window.FEATURE_CART_EXPIRY_COUNTDOWN_ENABLED =
  process.env.FEATURE_CART_EXPIRY_COUNTDOWN_ENABLED;

if (window.SENTRY_DSN && window.SENTRY_DSN !== "") {
  console.log("app init sentry ...");
  // Initialize Sentry
  Sentry.init({
    dsn: window.SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: window.SENTRY_TRACE_SAMPLE_RATE, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [window.SENTRY_TRACE_PROPAGATION_TARGETS],
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // A stale chunk after a deploy is expected to self-heal via the reload
    // in lazyWithReload/initChunkErrorRecovery, so drop the raw ChunkLoadError/
    // SyntaxError noise here. A repeat failure (reload didn't help) is
    // reported separately as its own distinct message-type event, which this
    // filter does not touch.
    beforeSend: chunkErrorSentryBeforeSend
  });
}

const App = memo(({ isLoggedUser, doLogout, backUrl }) => {
  const { t } = useTranslation();

  const onClickLogin = () => {
    doLoginBasicLogin(getBackURL());
  };

  const idToken = getIdToken();

  // get user pic from idtoken claims (IDP)
  let profile_pic = "";
  let profile_name = "";
  let profile_email = "";

  if (idToken) {
    const verifier = new IdTokenVerifier({
      issuer: window.IDP_BASE_URL,
      audience: window.OAUTH2_CLIENT_ID
    });
    const jwt = verifier.decode(idToken);
    profile_pic = jwt.payload.picture;
    profile_name =
      jwt.payload.name ||
      [jwt.payload.given_name, jwt.payload.family_name]
        .filter(Boolean)
        .join(" ");
    profile_email = jwt.payload.email || "";
  }

  return (
    <Sentry.ErrorBoundary
      fallback={SentryFallbackFunction({
        componentName: "Sponsor Services App"
      })}
    >
      <CustomTheme>
        <SnackbarNotification>
          <LoadingOverlay />
          <GlobalConfirmDialog />
          <BreadcrumbsProvider>
            <Router>
              <div>
                <NavBar
                  title={t("landing.sponsor_services")}
                  initLogOut={initLogOut}
                  isLoggedUser={isLoggedUser}
                  profileEmail={profile_email}
                  profileName={profile_name}
                  profilePic={profile_pic}
                  onClickLogin={onClickLogin}
                />
                <React.Suspense fallback={<div />}>
                  <Routes>
                    <Route
                      path="/app/*"
                      element={
                        <AuthorizedRoute
                          isLoggedUser={isLoggedUser}
                          backUrl={backUrl}
                          component={PrimaryLayout}
                        />
                      }
                    />
                    <Route
                      path="/auth/callback"
                      element={<AuthorizationCallbackRoute />}
                    />
                    <Route
                      path="/auth/logout"
                      element={<LogOutCallback doLogout={doLogout} />}
                    />
                    <Route path="/logout" element={<p>404 - Not Found</p>} />
                    <Route path="/404" element={<p>404 - Not Found</p>} />
                    <Route path="/error" element={<CustomErrorPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                      path="*"
                      element={<DefaultRoute isLoggedUser={isLoggedUser} />}
                    />
                  </Routes>
                </React.Suspense>
              </div>
            </Router>
          </BreadcrumbsProvider>
        </SnackbarNotification>
      </CustomTheme>
    </Sentry.ErrorBoundary>
  );
});

const mapStateToProps = ({ loggedUserState, baseState }) => ({
  isLoggedUser: loggedUserState.isLoggedUser,
  backUrl: loggedUserState.backUrl,
  member: loggedUserState.member,
  loading: baseState.loading
});

export default connect(mapStateToProps, {
  doLogout
})(App);
